import { useTexts } from '../../useTexts'

export type BarcodesByStatus = Record<
  uui.domain.client.rm.BarcodePod['barcodeStatus'],
  uui.domain.client.rm.BarcodePod[]
>

export function getBarcodesByStatus(podArray: uui.domain.client.rm.BarcodePod[]): BarcodesByStatus {
  const barcodesByStatusInitialValue: BarcodesByStatus = {
    SCANNED: [],
    UNREADABLE: [],
    MISSING_BARCODE: [],
    MISSING_PACKAGE: [],
    UNSCANNED: [],
  }

  if (!podArray || podArray.length === 0) {
    return barcodesByStatusInitialValue
  }

  return podArray.reduce((acc, pod) => {
    acc[pod.barcodeStatus] = [...acc[pod.barcodeStatus], pod]
    return acc
  }, barcodesByStatusInitialValue)
}

export function fromPodBarcodeToPodBarcodeOrUnscanned(
  podBarcodes: uui.domain.client.rm.BarcodePod[],
  orderStep: uui.domain.client.rm.OrderStep,
): uui.domain.client.rm.BarcodePod[] {
  const { barcodes: osBarcodes } = orderStep

  const clonePodBarcodes = [...podBarcodes]

  const barcodesInOs = osBarcodes.map(barcode => {
    const podIndex = clonePodBarcodes.findIndex(barcodePod => barcodePod.barcode === barcode)
    if (podIndex >= 0) {
      const result = clonePodBarcodes.splice(podIndex, 1)
      return result[0]
    }
    const unscanned: uui.domain.client.rm.BarcodePod = {
      uuid: 'unscanned',
      sec: -1,
      text: '',
      barcode,
      barcodeStatus: 'UNSCANNED',
      podType: 'barcodes',
      latLng: null,
    }
    return unscanned
  })

  return [...barcodesInOs, ...clonePodBarcodes]
}

export function getBarcodesStatusText(
  status: uui.domain.client.rm.BarcodePod['barcodeStatus'],
  texts: ReturnType<typeof useTexts>,
): string {
  const statusMap = {
    SCANNED: texts.scanned.label,
    UNREADABLE: texts.unreadable.label,
    MISSING_BARCODE: texts.missingBarcodes.label,
    MISSING_PACKAGE: texts.missingPackages.label,
    UNSCANNED: texts.unscanned.label,
  }
  return statusMap[status] || status
}

export type MergedBarcode = {
  barcode: string
  barcodeStatus: uui.domain.client.rm.BarcodePod['barcodeStatus']
  sec: number
  isDriverBarcode: boolean
}

export type MergedBarcodesByStatus = Record<
  uui.domain.client.rm.BarcodePod['barcodeStatus'],
  MergedBarcode[]
>

export function mergeBarcodesWithDriverBarcodes(
  regularBarcodes: uui.domain.client.rm.BarcodePod[],
  driverBarcodes: uui.domain.client.rm.DriverBarcodesPod[],
): MergedBarcodesByStatus {
  const merged: MergedBarcodesByStatus = {
    SCANNED: [],
    UNREADABLE: [],
    MISSING_BARCODE: [],
    MISSING_PACKAGE: [],
    UNSCANNED: [],
  }

  // Add regular barcodes
  for (const pod of regularBarcodes) {
    merged[pod.barcodeStatus].push({
      barcode: pod.barcode,
      barcodeStatus: pod.barcodeStatus,
      sec: pod.sec,
      isDriverBarcode: false,
    })
  }

  // Add driver barcodes (they don't have UNSCANNED status)
  for (const pod of driverBarcodes) {
    if (merged[pod.barcodeStatus]) {
      merged[pod.barcodeStatus].push({
        barcode: pod.barcode,
        barcodeStatus: pod.barcodeStatus,
        sec: pod.sec,
        isDriverBarcode: true,
      })
    }
  }

  return merged
}
